import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { parseCSV, type CSVParseResult } from './csvParser';
import { downloadCSVTemplate, REQUIRED_CSV_FIELDS, OPTIONAL_CSV_FIELDS } from './csvTemplate';
import { socialPostingService } from '../../services/socialPosting';

interface CSVImporterProps {
  postId: string;
  open: boolean;
  onClose: () => void;
  onImport: () => void;
}

export function CSVImporter({ postId, open, onClose, onImport }: CSVImporterProps) {
  const [parseResult, setParseResult] = useState<CSVParseResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<number | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setParseResult({ products: [], errors: ['File must be a .csv file'], detectedFields: [] });
      return;
    }
    setImportSuccess(null);
    setImportError(null);
    const result = await parseCSV(file);
    setParseResult(result);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleImport = async () => {
    if (!parseResult || parseResult.products.length === 0) return;
    try {
      setIsImporting(true);
      setImportError(null);
      await socialPostingService.addProducts(postId, parseResult.products);
      setImportSuccess(parseResult.products.length);
      setParseResult(null);
      onImport();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      setParseResult(null);
      setImportSuccess(null);
      setImportError(null);
      onClose();
    }
  };

  const hasErrors = !!(parseResult && parseResult.errors.length > 0 && parseResult.products.length === 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            Import Products from CSV
          </DialogTitle>
          <DialogDescription className="text-sm">
            Upload a CSV file to add products. Required fields: {REQUIRED_CSV_FIELDS.join(', ')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pt-3">
          {/* Download template */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
            <div>
              <p className="text-base font-medium">Need a template?</p>
              <p className="text-sm text-muted-foreground mt-1">
                Optional: {OPTIONAL_CSV_FIELDS.join(', ')} + any custom fields
              </p>
            </div>
            <Button variant="outline" className="rounded-lg h-10 px-4 shrink-0" onClick={downloadCSVTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Template
            </Button>
          </div>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`
              flex flex-col items-center justify-center gap-4 p-10 border-2 border-dashed rounded-xl cursor-pointer transition-colors
              ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-primary/40 hover:bg-muted/30'}
            `}
          >
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div className="text-center">
              <p className="text-base font-medium">Drop your CSV file here</p>
              <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.target.value = '';
              }}
            />
          </div>

          {/* Success */}
          {importSuccess !== null && (
            <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
              <p className="text-sm text-green-400">{importSuccess} product{importSuccess !== 1 ? 's' : ''} imported successfully!</p>
            </div>
          )}

          {/* Error */}
          {importError && (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
              <p className="text-sm text-red-400">{importError}</p>
            </div>
          )}

          {/* Parse result */}
          {parseResult && (
            <div className="space-y-4">
              {parseResult.errors.length > 0 && (
                <div className="space-y-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  {parseResult.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-red-400">{err}</p>
                    </div>
                  ))}
                </div>
              )}

              {parseResult.products.length > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-base font-medium">
                      {parseResult.products.length} product{parseResult.products.length !== 1 ? 's' : ''} ready
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {parseResult.detectedFields.slice(0, 8).map((field) => (
                        <Badge key={field} variant="secondary" className="text-xs">
                          {field}
                        </Badge>
                      ))}
                      {parseResult.detectedFields.length > 8 && (
                        <Badge variant="secondary" className="text-xs">
                          +{parseResult.detectedFields.length - 8}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="border rounded-xl overflow-auto max-h-48">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50 border-b">
                          {parseResult.detectedFields.slice(0, 6).map((field) => (
                            <th key={field} className="text-left px-4 py-2.5 font-medium text-muted-foreground whitespace-nowrap">
                              {field}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parseResult.products.slice(0, 5).map((p, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            {parseResult.detectedFields.slice(0, 6).map((field) => (
                              <td key={field} className="px-4 py-2.5 truncate max-w-[220px]">
                                {String(p.raw_data[field] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parseResult.products.length > 5 && (
                      <p className="text-sm text-muted-foreground text-center py-2.5 bg-muted/30">
                        ...and {parseResult.products.length - 5} more
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {parseResult && parseResult.products.length > 0 && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" className="rounded-lg h-10 px-5 text-sm" onClick={() => setParseResult(null)}>
              Clear
            </Button>
            <Button className="rounded-lg h-10 px-5 text-sm" onClick={handleImport} disabled={isImporting || hasErrors}>
              {isImporting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Import {parseResult.products.length} Product{parseResult.products.length !== 1 ? 's' : ''}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
